import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import { PapiUser } from '@wix/da-papi-types';
import EllipsisIcon from '@wix/da-ds/pkg/Icons/Ellipsis';
import {
  DropdownOption,
  NullableDropdownGroup,
} from '@wix/da-shared-react/pkg/Dropdown/types';
import IconButtonWithContextMenu from '@wix/da-shared-react/pkg/Dropdown/Preset/IconButtonWithContextMenuV2';
import { SectionContext } from '../../../../../../Sections/SectionContext';

export interface Props {
  profileOwnerUser: PapiUser;
  isGroupMember: boolean;
  canInviteToGroup: boolean;
  canChangeFounder: boolean;
  canLeaveGroup: boolean;
  canCloseGroup: boolean;
  canMessageAdmins: boolean;
  canRespondToGroupNotifications: boolean;
  requestChangeFounder: (group: PapiUser) => void;
  requestLeaveGroup: (group: PapiUser) => void;
  showCloseGroupModal: (group: PapiUser) => void;
  unwatchGroup: (group: PapiUser) => void;
  inviteToGroup: (group: PapiUser) => void;
}

const GroupMoreMenu: React.FC<Props> = ({
  canRespondToGroupNotifications,
  profileOwnerUser,
  isGroupMember,
  canMessageAdmins,
  canInviteToGroup,
  canChangeFounder,
  canLeaveGroup,
  canCloseGroup,
  requestLeaveGroup,
  requestChangeFounder,
  showCloseGroupModal,
  unwatchGroup,
  inviteToGroup,
}) => {
  const { t } = useTranslation();
  const section = useContext(SectionContext);
  const isBackroomSection = section === 'backroom';
  const menuItems: NullableDropdownGroup[] = [];

  const communicationMenuGroup = {
    items: [] as DropdownOption[],
  };
  const farewellMenuGroup = {
    items: [] as DropdownOption[],
  };

  if (canMessageAdmins) {
    communicationMenuGroup.items.push({
      label: t('navigation.subnavigation.messageAdmins'),
      onClick: () =>
        window.location.assign(Url.groupnoteLink(profileOwnerUser.username)),
    });
  }

  if (canRespondToGroupNotifications) {
    communicationMenuGroup.items.push({
      label: t('groups.admin.groupNotifications'),
      link: Url.groupNotificationsLink(profileOwnerUser.userId),
    });
  }

  if (canInviteToGroup) {
    communicationMenuGroup.items.push({
      label: t('groups.sendInvite'),
      onClick: () => inviteToGroup(profileOwnerUser),
    });
  }

  if (canLeaveGroup || canChangeFounder) {
    farewellMenuGroup.items.push({
      label: t('groups.leaveGroup'),
      onClick: () => requestLeaveGroup(profileOwnerUser),
    });
  }

  if (isGroupMember) {
    farewellMenuGroup.items.push({
      label: t('navigation.subnavigation.unwatchGroup'),
      onClick: () => unwatchGroup(profileOwnerUser),
    });
  }

  if (canCloseGroup) {
    if (isBackroomSection) {
      farewellMenuGroup.items.push({
        label: t('groups.admin.closeGroup'),
        onClick: () => showCloseGroupModal(profileOwnerUser),
      });
    }
  }

  const isEmpty =
    communicationMenuGroup.items.length + farewellMenuGroup.items.length === 0;
  if (isEmpty) {
    return null;
  }
  menuItems.push(communicationMenuGroup);
  menuItems.push(farewellMenuGroup);

  return (
    <IconButtonWithContextMenu
      size="large"
      items={menuItems}
      icon={EllipsisIcon}
      placement="bottom-end"
    />
  );
};
GroupMoreMenu.displayName = 'GroupMoreMenu';

export default GroupMoreMenu;
